import React from "react";
import {
  Grid,
  Link,
  Button,
  Typography,
  Box,
  ThemeProvider,
  Card,
  CardHeader,
  CardContent,
} from "@mui/material";
import { createTheme } from "@mui/material/styles";
import { Add as AddIcon } from "@mui/icons-material";
import Snackbars from "./Snackbars";
import BreadcrumbsComponent from "./BreadcrumbsComponent";
import { blue } from "@mui/material/colors";
import NestedCardsWrapper from "./NestedCardsWrapper";

const theme = createTheme({
  palette: {
    primary: {
      main: "#1e5d45", // Custom primary color
    },
  },
  components: {
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          "&.Mui-error": {
            color: "#f44336", // Custom error color
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        "@media (min-width:1200px)": {
          body: {
            fontSize: "12px", // Smaller font size for lg screens
          },
        },
        "@media (max-width:1199px)": {
          body: {
            fontSize: "14px", // Default font size for smaller screens
          },
        },
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        size: "small", // Set size for Autocomplete globally
      },
    },
    MuiTextField: {
      defaultProps: {
        size: "small", // Set size for TextField globally
      },
    },
    MuiSelect: {
      defaultProps: {
        size: "small", // Set size for Select globally
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          fontSize: "30px", // Default icon size
          marginRight: "8px", // Spacing to the right of the icon
        },
      },
    },
    MuiTable: {
      defaultProps: {
        size: "small", // All Tables will be small by default
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: "4px", // All TableCells will have 4px padding by default
        },
      },
    },
  },
  typography: {
    fontSize: 14,
    h6: {
      "@media (max-width:1199px)": {
        fontSize: "1rem", // Default font size for smaller screens
      },
    },
    body1: {
      fontSize: "0.875rem", // Default font size for body1
      "@media (min-width:1200px)": {
        fontSize: "0.875rem", // Larger font size for lg screens
      },
      "@media (max-width:1199px)": {
        fontSize: "0.875rem", // Default font size for smaller screens
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
});

// NOTE: This reusable component serves as a wrapper for "manage" routes (e.g., /manage-service-reports, /manage-job-orders, /manage-users) to provide a consistent layout and styling.

const BaseWrapper = ({
  children,
  titleIcon,
  title,
  subTitle,
  subTitleRoute,
  createButtonLabel,
  onClickCreateButton,
  // NOTE: make sure to always pass these props below for snackbars to show
  successMessage,
  setSuccessMessage,
  errorMessage,
  setErrorMessage,
  isLoading,
}) => {
  return (
    <ThemeProvider theme={theme}>
      <Snackbars
        successMessage={successMessage}
        setSuccessMessage={setSuccessMessage}
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
        isLoading={isLoading}
      />

      <Box>
        <Card
          variant="outlined"
          style={{
            marginTop: "20px",
            marginBottom: "20px",
            borderRadius: 0,
          }}
        >
          <CardHeader
            title={
              <Grid
                container
                alignItems="center"
                justifyContent="space-between"
              >
                <Grid item xs={12} md="auto">
                  <Box display="flex" alignItems="center">
                    {titleIcon}
                    <Typography
                      variant="h6"
                      sx={{ fontSize: { xs: "1rem", md: "1.25rem" } }}
                    >
                      {title}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md="auto">
                  <BreadcrumbsComponent
                    title={title}
                    subTitle={subTitle}
                    subTitleRoute={subTitleRoute}
                    showFormTitle={false}
                  />
                </Grid>
              </Grid>
            }
            sx={{
              backgroundColor: "#1e5d45",
              color: "white",
              padding: { xs: "8px", md: "16px" },
            }}
          />
          <CardContent sx={{ padding: { xs: "10px", md: "10px" } }}>
            <Card
              variant="outlined"
              sx={{ padding: { xs: "10px", md: "10px" } }}
            >
              <CardHeader
                title={
                  <Grid container alignItems="center" spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        {subTitle}
                      </Typography>
                    </Grid>
                    {typeof createButtonLabel !== "undefined" && (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        container
                        justifyContent={{ xs: "center", sm: "flex-end" }}
                      >
                        <Button
                          component={Link}
                          onClick={onClickCreateButton}
                          variant="contained"
                          color="primary"
                          startIcon={<AddIcon />}
                        >
                          {createButtonLabel}
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                }
                sx={{
                  paddingLeft: 0,
                  paddingRight: 0,
                  paddingBottom: "10px"
                }}
              />
              <NestedCardsWrapper>{children}</NestedCardsWrapper>
            </Card>
          </CardContent>
        </Card>
      </Box>
    </ThemeProvider>
  );
};

export default BaseWrapper;
